import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Icon } from './icons/Icon';
import './Modal.css';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string | ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    closeOnOverlayClick?: boolean;
    /** Örn. date-range popup: overlay stilini özelleştirmek için */
    overlayClassName?: string;
    /**
     * Başlıkta tam-ekran büyüt/küçült (maximize) düğmesi göster.
     * Varsayılan: küçük (`sm`) ve zaten tam (`full`) boyut hariç AÇIK.
     */
    maximizable?: boolean;
}

// Material "open_in_full" / "close_fullscreen" — büyüt / eski boyuta dön
const MAXIMIZE_PATH = 'M3 3h6v2H5v4H3V3zm12 0h6v6h-2V5h-4V3zM3 15h2v4h4v2H3v-6zm16 0h2v6h-6v-2h4v-4z';
const RESTORE_PATH = 'M5 5h4V3H3v6h2V5zm14-2h-6v2h4v4h2V3zM5 19v-4H3v6h6v-2H5zm14 0h-4v2h6v-6h-2v4z';

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    closeOnOverlayClick = true,
    overlayClassName,
    maximizable,
}) => {
    const overlayPointerDownRef = useRef(false);
    const [maximized, setMaximized] = useState(false);

    const canMaximize = maximizable ?? (size !== 'sm' && size !== 'full');

    // ESC tuşu ile kapatma
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Kapanınca büyütme durumunu sıfırla
    useEffect(() => {
        if (!isOpen) setMaximized(false);
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className={['modal-overlay', overlayClassName].filter(Boolean).join(' ')}
            onPointerDown={(event) => {
                overlayPointerDownRef.current = event.target === event.currentTarget;
            }}
            onClick={closeOnOverlayClick ? (event) => {
                const shouldClose =
                    overlayPointerDownRef.current &&
                    event.target === event.currentTarget;
                overlayPointerDownRef.current = false;
                if (shouldClose) {
                    onClose();
                }
            } : undefined}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`modal-content modal-size-${size}${maximized ? ' modal-maximized' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header">
                    {typeof title === 'string' ? <h2>{title}</h2> : title}
                    <div className="modal-header-actions">
                        {canMaximize && (
                            <button
                                type="button"
                                className="modal-header-btn"
                                onClick={() => setMaximized((v) => !v)}
                                aria-label={maximized ? 'Eski boyut' : 'Tam ekran'}
                                title={maximized ? 'Eski boyut' : 'Tam ekran'}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d={maximized ? RESTORE_PATH : MAXIMIZE_PATH} />
                                </svg>
                            </button>
                        )}
                        <button className="modal-close modal-header-btn" onClick={onClose} aria-label="Kapat">
                            <Icon name="close" size={20} />
                        </button>
                    </div>
                </div>

                <div className="modal-body">
                    {children}
                </div>

                {footer && (
                    <div className="modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
